import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterMode,
  DriveMode,
  EngineProfileId,
  SpeedUnit,
  TransmissionMode,
  VehicleState,
} from '../types';
import { SpeedProcessor } from '../vehicle/SpeedProcessor';
import { AccelerationProcessor } from '../vehicle/AccelerationProcessor';
import { Gearbox, ShiftEvent } from '../vehicle/Gearbox';
import { EngineSimulator } from '../vehicle/EngineSimulator';
import { AudioEngine } from '../audio/AudioEngine';
import { GeolocationService, GPSUpdate } from '../vehicle/GeolocationService';
import { KMH_TO_MPH } from '../utils/speed';
import { BMW_M340I_ZF8HP_PROFILE } from '../config/transmissions';
import { isEVProfile } from '../config/engines';

export function useVehicleSimulator() {
  // Engine and Vehicle Singletons stored in refs to avoid React re-render recreation
  const speedProcessorRef = useRef<SpeedProcessor | null>(null);
  const accelerationProcessorRef = useRef<AccelerationProcessor | null>(null);
  const gearboxRef = useRef<Gearbox | null>(null);
  const engineSimulatorRef = useRef<EngineSimulator | null>(null);
  const audioEngineRef = useRef<AudioEngine | null>(null);
  const geoServiceRef = useRef<GeolocationService | null>(null);

  // Mutable state for the high-frequency loop
  const rawInputSpeedKmhRef = useRef<number>(0);
  const rawInputAccelerationRef = useRef<number>(0); // -1.0 to +1.0
  const hasManualAccelerationRef = useRef<boolean>(true); // Controlled via slider in Demo mode
  const isDemoModeRef = useRef<boolean>(false); // Production vehicle defaults to real GPS tracking
  const isEngineRunningRef = useRef<boolean>(false);
  const currentEngineProfileRef = useRef<EngineProfileId>('v8');
  const currentDriveModeRef = useRef<DriveMode>('COMFORT');
  const animationFrameRef = useRef<number | null>(null);
  const lastUiUpdateRef = useRef<number>(0);

  // User-facing React State (throttled to ~25Hz to keep React snappy while audio runs at full speed)
  const [vehicleState, setVehicleState] = useState<VehicleState>({
    speedKmh: 0,
    speedMph: 0,
    targetRpm: 750,
    currentRpm: 750,
    rpm: 750,
    targetRPM: 750,
    gear: 1,
    totalGears: 8,
    transmissionMode: 'automatic',
    mode: 'COMFORT',
    shiftState: 'IDLE',
    shiftTimerMs: 0,
    throttle: 0,
    wheelRpm: 0,
    gearRatio: 5.25,
    finalDriveRatio: 2.81,
    acceleration: 0,
    effort: 0,
    isBraking: false,
    engineBrakingIntensity: 0,
    isRedline: false,
    isShifting: false,
    engineProfile: 'v8',
    isEV: false,
    characterMode: 'sport',
    isEngineRunning: false,
    isMuted: false,
    volume: 0.85,
    isDemoMode: false,
    gpsStatus: 'idle',
    gpsAccuracy: null,
    audioDebug: {
      baseFrequencyHz: 50,
      fundamentalGain: 0.35,
      harmonicsGain: 0.35,
      lowFreqBodyGain: 0.7,
      intakeGain: 0,
      exhaustGain: 1.0,
      mechanicalNoiseGain: 0.04,
      highFreqRaspGain: 0,
      engineBrakingWhineGain: 0,
      filterCutoffHz: 360,
      filterQ: 2.4,
      saturationDrive: 0.9,
      masterSynthGain: 0.7,
      currentRpmBand: 'idle',
      audioContextState: 'uninitialized',
      sampleRate: 48000,
    },
  });

  const [speedUnit, setSpeedUnit] = useState<SpeedUnit>('kmh');

  // Initialize instances once
  useEffect(() => {
    const gearbox = new Gearbox({ profile: BMW_M340I_ZF8HP_PROFILE }, 'automatic', 'COMFORT');
    const speedProcessor = new SpeedProcessor();
    const accelerationProcessor = new AccelerationProcessor();
    const engineSimulator = new EngineSimulator(gearbox, 'v8');
    const audioEngine = new AudioEngine();
    const geoService = new GeolocationService();

    gearboxRef.current = gearbox;
    speedProcessorRef.current = speedProcessor;
    accelerationProcessorRef.current = accelerationProcessor;
    engineSimulatorRef.current = engineSimulator;
    audioEngineRef.current = audioEngine;
    geoServiceRef.current = geoService;

    // Listen to gearbox shifts for audio feedback
    const unsubscribeShift = gearbox.addShiftListener((event: ShiftEvent) => {
      audioEngine.onShift(event.direction, event.mode);
    });

    // Immediately trigger browser GPS location request upon app load (before Start Engine)
    if (!isDemoModeRef.current) {
      geoService.start((update: GPSUpdate) => {
        let currentKmh = 0;
        if (update.speedMs !== null) {
          currentKmh = speedProcessor.processSpeed(update.speedMs, false, update.timestamp);
        } else {
          currentKmh = speedProcessor.getSpeed();
        }
        setVehicleState(prev => ({
          ...prev,
          gpsStatus: update.status,
          gpsAccuracy: update.accuracy,
          speedKmh: isEngineRunningRef.current ? prev.speedKmh : currentKmh,
          speedMph: isEngineRunningRef.current ? prev.speedMph : currentKmh * KMH_TO_MPH,
        }));
      });
    }

    return () => {
      unsubscribeShift();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      geoService.stop();
      audioEngine.destroy();
      engineSimulator.destroy();
    };
  }, []);

  // Main high-frequency simulation loop running on requestAnimationFrame
  const runSimulationTick = useCallback(() => {
    const now = performance.now();
    const speedProc = speedProcessorRef.current;
    const accelProc = accelerationProcessorRef.current;
    const gearbox = gearboxRef.current;
    const engineSim = engineSimulatorRef.current;
    const audio = audioEngineRef.current;

    if (speedProc && accelProc && gearbox && engineSim && audio && isEngineRunningRef.current) {
      // 1. Determine current speed
      let smoothedSpeedKmh = 0;
      let accelResult = {
        acceleration: 0,
        effort: 0,
        isBraking: false,
        engineBrakingIntensity: 0,
      };

      if (isDemoModeRef.current) {
        // In Demo mode, process demo slider speed directly
        smoothedSpeedKmh = speedProc.processSpeed(rawInputSpeedKmhRef.current, true, now);
        if (hasManualAccelerationRef.current) {
          accelResult = accelProc.setManualAcceleration(rawInputAccelerationRef.current);
        } else {
          accelResult = accelProc.process(smoothedSpeedKmh, now);
        }
      } else {
        // Real GPS Mode
        smoothedSpeedKmh = speedProc.getSpeed();
        accelResult = accelProc.process(smoothedSpeedKmh, now);
      }

      const isEV = isEVProfile(currentEngineProfileRef.current);

      if (isEV) {
        // Direct EV Propulsion: Speed + Acceleration + Throttle/Load + Driving Mode
        // No gears, no RPM, no redline, no virtual ratios
        const normalizedThrottle = Math.max(0, accelResult.effort);
        const brakeInput = accelResult.isBraking
          ? Math.max(accelResult.engineBrakingIntensity, Math.abs(Math.min(0, accelResult.acceleration)))
          : (accelResult.acceleration < -0.05 ? Math.abs(accelResult.acceleration) : 0);

        const evState = audio.updateEVParameters(
          smoothedSpeedKmh,
          accelResult.acceleration,
          normalizedThrottle,
          brakeInput,
          currentDriveModeRef.current
        );

        // Throttled UI State update (~25 updates/sec)
        if (now - lastUiUpdateRef.current > 38) {
          lastUiUpdateRef.current = now;
          const evDebug = audio.getEVDebugInfo();
          const evMusical = audio.getEVMusicalState();
          setVehicleState(prev => ({
            ...prev,
            speedKmh: smoothedSpeedKmh,
            speedMph: smoothedSpeedKmh * KMH_TO_MPH,
            targetRpm: 0,
            targetRPM: 0,
            currentRpm: 0,
            rpm: 0,
            gear: 1,
            shiftState: 'IDLE',
            shiftTimerMs: 0,
            throttle: normalizedThrottle,
            acceleration: accelResult.acceleration,
            effort: accelResult.effort,
            wheelRpm: 0,
            isBraking: accelResult.isBraking,
            engineBrakingIntensity: accelResult.engineBrakingIntensity,
            isRedline: false,
            isShifting: false,
            isEV: true,
            evSoundState: evState || (evDebug ? {
              speedKmh: smoothedSpeedKmh,
              acceleration: accelResult.acceleration,
              throttle: normalizedThrottle,
              mode: evDebug.activeMode,
              intensity: evDebug.intensityPct / 100,
              pitchHz: evDebug.baseFrequencyHz,
              spatialWidth: evDebug.spatialWidthPct / 100,
              regenIntensity: evDebug.regenIntensityPct / 100,
            } : undefined),
            evMusicalState: evMusical || undefined,
            evDebug: evDebug || undefined,
            audioDebug: audio.getDebugInfo(),
          }));
        }
      } else {
        // 2. Update Engine Simulator RPM (Combustion)
        const { currentRpm, targetRpm, isRedline, wheelRpm } = engineSim.update(
          smoothedSpeedKmh,
          accelResult.effort,
          accelResult.acceleration,
          accelResult.isBraking,
          accelResult.engineBrakingIntensity,
          now
        );

        // Momentary load reduction during upshift to simulate clutch cut
        const isUpshift = gearbox.getShiftState() === 'UPSHIFT';
        const effectiveAudioEffort = isUpshift ? accelResult.effort * 0.35 : accelResult.effort;

        // 3. Update Web Audio API directly (Zero React overhead)
        audio.setRPM(currentRpm);
        audio.setThrottle(effectiveAudioEffort);
        audio.setEngineBraking(accelResult.engineBrakingIntensity);

        // 4. Throttled UI State update (~25 updates/sec is super smooth for LCD gauge and uses minimal React CPU)
        if (now - lastUiUpdateRef.current > 38) {
          lastUiUpdateRef.current = now;
          const normalizedThrottle = Math.max(0, accelResult.effort);
          setVehicleState(prev => ({
            ...prev,
            speedKmh: smoothedSpeedKmh,
            speedMph: smoothedSpeedKmh * KMH_TO_MPH,
            targetRpm,
            targetRPM: targetRpm,
            currentRpm,
            rpm: currentRpm,
            gear: gearbox.getGear(),
            totalGears: gearbox.getGearCount(),
            transmissionMode: gearbox.getMode(),
            mode: gearbox.getDriveMode(),
            shiftState: gearbox.getShiftState(),
            shiftTimerMs: Math.round(gearbox.getShiftTimerMs()),
            throttle: normalizedThrottle,
            acceleration: accelResult.acceleration,
            effort: accelResult.effort,
            wheelRpm: Math.round(wheelRpm * 10) / 10,
            gearRatio: gearbox.getRatio(),
            finalDriveRatio: gearbox.getFinalDriveRatio(),
            isBraking: accelResult.isBraking,
            engineBrakingIntensity: accelResult.engineBrakingIntensity,
            isRedline,
            isShifting: gearbox.isShiftInProgress(),
            isEV: false,
            predictedDownshiftRpm: gearbox.getPredictedDownshiftRpm(currentRpm) ?? undefined,
            isDownshiftCoolingDown: gearbox.isDownshiftCoolingDown(now),
            isDemandDownshiftActive: gearbox.isDemandDownshiftActive(),
            audioDebug: audio.getDebugInfo(),
          }));
        }
      }
    }

    if (isEngineRunningRef.current) {
      animationFrameRef.current = requestAnimationFrame(runSimulationTick);
    }
  }, []);

  // GPS Tracking Handler
  const startGpsTracking = useCallback(() => {
    const geo = geoServiceRef.current;
    const speedProc = speedProcessorRef.current;
    if (!geo || !speedProc) return;

    geo.start((update: GPSUpdate) => {
      let currentKmh = 0;
      if (update.speedMs !== null) {
        currentKmh = speedProc.processSpeed(update.speedMs, false, update.timestamp);
      } else {
        currentKmh = speedProc.getSpeed();
      }
      setVehicleState(prev => ({
        ...prev,
        gpsStatus: update.status,
        gpsAccuracy: update.accuracy,
        speedKmh: isEngineRunningRef.current ? prev.speedKmh : currentKmh,
        speedMph: isEngineRunningRef.current ? prev.speedMph : currentKmh * KMH_TO_MPH,
      }));
    });
  }, []);

  // START ENGINE: Unlocks Web Audio with user gesture and starts the simulation loop
  const startEngine = useCallback(async () => {
    const audio = audioEngineRef.current;
    if (!audio) return;

    const initialized = await audio.initialize();
    if (!initialized) {
      console.warn('Audio initialization failed.');
      return;
    }

    await audio.start();
    isEngineRunningRef.current = true;

    // If in GPS mode, start geolocation watch
    if (!isDemoModeRef.current) {
      startGpsTracking();
    }

    setVehicleState(prev => ({
      ...prev,
      isEngineRunning: true,
      gpsStatus: isDemoModeRef.current ? 'demo' : (prev.gpsStatus === 'idle' ? 'searching' : prev.gpsStatus),
    }));

    // Start simulation loop
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(runSimulationTick);
  }, [runSimulationTick, startGpsTracking]);

  // STOP ENGINE
  const stopEngine = useCallback(() => {
    isEngineRunningRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioEngineRef.current) {
      audioEngineRef.current.stop();
    }
    // Keep GPS tracking warm and running so speedometer continues reading live vehicle speed
    setVehicleState(prev => ({
      ...prev,
      isEngineRunning: false,
      targetRpm: 0,
      currentRpm: 0,
      effort: 0,
      isBraking: false,
    }));
  }, []);

  // Toggle Demo Mode vs GPS Mode
  const setDemoMode = useCallback((enabled: boolean) => {
    isDemoModeRef.current = enabled;
    const geo = geoServiceRef.current;
    const speedProc = speedProcessorRef.current;

    if (enabled) {
      // Switch to demo mode: stop GPS watch
      if (geo) geo.stop();
      setVehicleState(prev => ({
        ...prev,
        isDemoMode: true,
        gpsStatus: 'demo',
        gpsAccuracy: null,
      }));
    } else {
      // Switch to real GPS mode
      if (speedProc) speedProc.reset(0);
      rawInputSpeedKmhRef.current = 0;
      setVehicleState(prev => ({
        ...prev,
        isDemoMode: false,
        gpsStatus: 'searching',
      }));
      startGpsTracking();
    }
  }, [startGpsTracking]);

  // Demo Controls
  const setDemoSpeed = useCallback((kmh: number) => {
    rawInputSpeedKmhRef.current = Math.max(0, Math.min(240, kmh));
  }, []);

  const setDemoEffort = useCallback((effort: number) => {
    rawInputAccelerationRef.current = Math.max(-1.0, Math.min(1.0, effort));
    hasManualAccelerationRef.current = true;
  }, []);

  const setDemoAcceleration = useCallback((accel: number) => {
    const clamped = Math.max(-1.0, Math.min(1.0, accel));
    rawInputAccelerationRef.current = clamped;
    hasManualAccelerationRef.current = true;
    if (accelerationProcessorRef.current) {
      const res = accelerationProcessorRef.current.setManualAcceleration(clamped);
      if (audioEngineRef.current) {
        if (isEVProfile(currentEngineProfileRef.current)) {
          const speedProc = speedProcessorRef.current;
          const currentSpeed = speedProc ? (isDemoModeRef.current ? rawInputSpeedKmhRef.current : speedProc.getSpeed()) : 0;
          const brake = res.isBraking ? res.engineBrakingIntensity : 0;
          audioEngineRef.current.updateEVParameters(
            currentSpeed,
            res.acceleration,
            res.effort,
            brake,
            currentDriveModeRef.current
          );
        } else {
          audioEngineRef.current.setThrottle(res.effort);
          audioEngineRef.current.setEngineBraking(res.engineBrakingIntensity);
        }
      }
    }
  }, []);

  const setNeutralRev = useCallback((effort: number) => {
    if (isEVProfile(currentEngineProfileRef.current)) return;
    if (engineSimulatorRef.current) {
      engineSimulatorRef.current.setNeutralRev(effort);
    }
  }, []);

  // Transmission Controls (combustion only)
  const shiftUp = useCallback(() => {
    if (isEVProfile(currentEngineProfileRef.current)) return;
    if (gearboxRef.current) {
      gearboxRef.current.shiftUp();
      setVehicleState(prev => ({ ...prev, gear: gearboxRef.current!.getGear() }));
    }
  }, []);

  const shiftDown = useCallback(() => {
    if (isEVProfile(currentEngineProfileRef.current)) return;
    if (gearboxRef.current) {
      gearboxRef.current.shiftDown();
      setVehicleState(prev => ({ ...prev, gear: gearboxRef.current!.getGear() }));
    }
  }, []);

  const setGear = useCallback((gear: number) => {
    if (isEVProfile(currentEngineProfileRef.current)) return;
    if (gearboxRef.current) {
      gearboxRef.current.setGear(gear);
      gearboxRef.current.setMode('manual');
      setVehicleState(prev => ({
        ...prev,
        gear: gearboxRef.current!.getGear(),
        transmissionMode: 'manual',
      }));
    }
  }, []);

  const setTransmissionMode = useCallback((mode: TransmissionMode) => {
    if (isEVProfile(currentEngineProfileRef.current)) return;
    if (gearboxRef.current) {
      gearboxRef.current.setMode(mode);
      setVehicleState(prev => ({ ...prev, transmissionMode: mode }));
    }
  }, []);

  // Drive Mode: ECO, COMFORT, SPORT
  const setDriveMode = useCallback((mode: DriveMode) => {
    currentDriveModeRef.current = mode;
    if (gearboxRef.current) {
      gearboxRef.current.setDriveMode(mode);
    }
    if (audioEngineRef.current) {
      audioEngineRef.current.setDriveMode(mode);
    }
    setVehicleState(prev => ({ ...prev, mode }));
  }, []);

  // Engine Profile
  const setEngineProfile = useCallback((profileId: EngineProfileId) => {
    currentEngineProfileRef.current = profileId;
    const isEV = isEVProfile(profileId);
    if (!isEV && engineSimulatorRef.current) {
      engineSimulatorRef.current.setProfile(profileId);
    }
    if (audioEngineRef.current) {
      audioEngineRef.current.setEngineProfile(profileId);
    }
    const currentGearbox = gearboxRef.current;
    setVehicleState(prev => ({
      ...prev,
      engineProfile: profileId,
      isEV,
      totalGears: currentGearbox ? currentGearbox.getGearCount() : prev.totalGears,
      gear: currentGearbox ? currentGearbox.getGear() : prev.gear,
      gearRatio: currentGearbox ? currentGearbox.getRatio() : prev.gearRatio,
      finalDriveRatio: currentGearbox ? currentGearbox.getFinalDriveRatio() : prev.finalDriveRatio,
    }));
  }, []);

  // Character Mode
  const setCharacterMode = useCallback((character: CharacterMode) => {
    if (audioEngineRef.current) {
      audioEngineRef.current.setCharacterMode(character);
    }
    setVehicleState(prev => ({ ...prev, characterMode: character }));
  }, []);

  // Master Volume
  const setVolume = useCallback((volume: number) => {
    if (audioEngineRef.current) {
      audioEngineRef.current.setVolume(volume);
    }
    setVehicleState(prev => ({ ...prev, volume }));
  }, []);

  // Mute Toggle
  const toggleMute = useCallback(() => {
    if (audioEngineRef.current) {
      const nextMute = !vehicleState.isMuted;
      audioEngineRef.current.setMuted(nextMute);
      setVehicleState(prev => ({ ...prev, isMuted: nextMute }));
    }
  }, [vehicleState.isMuted]);

  return {
    vehicleState,
    speedUnit,
    setSpeedUnit,
    startEngine,
    stopEngine,
    setDemoMode,
    setDemoSpeed,
    setDemoEffort,
    setDemoAcceleration,
    setNeutralRev,
    shiftUp,
    shiftDown,
    setGear,
    setTransmissionMode,
    setDriveMode,
    setEngineProfile,
    setCharacterMode,
    setVolume,
    toggleMute,
  };
}
